<template>
  <section class="home-section" id="home">
    <!-- Video background only without poster -->
    <video
      ref="backgroundVideo"
      class="video-background"
      autoplay
      muted
      loop
      playsinline
      preload="auto"
    >
      <source src="@/assets/Home/background-video.webm" type="video/webm" />
      <!-- Fallback source for better browser compatibility -->
      <source src="@/assets/Home/background-video.mp4" type="video/mp4" />
    </video>

    <!-- Overlay gradient -->
    <div class="overlay"></div>

    <div class="container">
      <!-- Main Content with animated reveal -->
      <div
        class="content-box"
        data-aos="zoom-in"
        data-aos-duration="800"
        data-aos-delay="100"
      >
        <h5 class="pre-title" data-aos="fade-down" data-aos-delay="200">
          Welcome to
        </h5>
        <h1 class="main-title" data-aos="fade-up" data-aos-delay="300">
          Your Cars LLC
        </h1>
        <div
          class="title-underline"
          data-aos="slide-right"
          data-aos-delay="400"
        ></div>
        <p class="main-tagline" data-aos="fade-up" data-aos-delay="500">
          Premium Auto Dealership
        </p>

        <!-- Contact Information with animation -->
        <div class="brief-info">
          <div
            class="info-item"
            data-aos="fade-up"
            data-aos-duration="1000"
            data-aos-delay="600"
          >
            <div class="info-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            </div>
            <span>GLENDALE, CA 91204</span>
          </div>

          <div
            class="info-item"
            data-aos="fade-up"
            data-aos-duration="1000"
            data-aos-delay="700"
          >
            <div class="info-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                ></path>
              </svg>
            </div>
            <span>(424) 333-4220</span>
          </div>

          <p
            class="tagline-text"
            data-aos="fade-up"
            data-aos-duration="1000"
            data-aos-delay="800"
          >
            Your trusted partner in finding your dream car
          </p>
        </div>

        <!-- Call to Action Buttons with animation -->
        <div
          class="cta-buttons"
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-delay="900"
        >
          <router-link to="/buy" class="cta-button inventory-button">
            <span>View cars</span>
            <span class="button-icon">→</span>
          </router-link>

          <a
            href="https://forms.gle/svUoqeg4JnzgtyvH8"
            target="_blank"
            class="cta-button application-button"
          >
            <span>Submit Application</span>
            <span class="button-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
            </span>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import AOS from "aos";
import "aos/dist/aos.css";

export default {
  name: "HomeSection",
  data() {
    return {
      videoLoaded: false,
      videoError: false,
    };
  },
  methods: {
    initVideo() {
      const video = this.$refs.backgroundVideo;
      if (video) {
        // Add event listeners to track video state
        video.addEventListener("loadeddata", () => {
          this.videoLoaded = true;
          this.playVideo();
        });

        video.addEventListener("error", () => {
          this.videoError = true;
          console.error("Video loading error");
          this.handleVideoError();
        });

        // Initial attempt to play
        this.playVideo();
      }
    },

    playVideo() {
      const video = this.$refs.backgroundVideo;
      if (video) {
        // Use Promise to handle autoplay restrictions
        const playPromise = video.play();

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Autoplay started successfully
              console.log("Background video playing successfully");
            })
            .catch((error) => {
              console.warn("Autoplay prevented:", error);
              this.handleAutoplayRestriction();
            });
        }
      }
    },

    handleAutoplayRestriction() {
      // Add a UI indicator for user to interact if autoplay is blocked
      const section = document.getElementById("home");
      if (section && !document.getElementById("video-play-btn")) {
        const playButton = document.createElement("button");
        playButton.id = "video-play-btn";
        playButton.className = "video-play-button";
        playButton.innerHTML = "Play Background";
        playButton.addEventListener("click", () => {
          this.playVideo();
          playButton.remove();
        });
        section.appendChild(playButton);
      }
    },

    handleVideoError() {
      // Apply a fallback background if video fails
      const section = document.getElementById("home");
      if (section) {
        section.classList.add("video-fallback");
      }
    },

    handleResize() {
      // Adjust video positioning on resize if needed
      const video = this.$refs.backgroundVideo;
      if (video && window.innerWidth / window.innerHeight > 16 / 9) {
        // If screen aspect ratio is wider than video
        video.style.width = "100%";
        video.style.height = "auto";
      } else {
        // If screen aspect ratio is taller than video
        video.style.width = "auto";
        video.style.height = "100%";
      }
    },
  },
  mounted() {
    // Initialize AOS animation library
    AOS.init({
      once: true,
      offset: 50,
      disable: window.innerWidth < 768 ? true : false, // Disable on mobile for performance
    });

    // Initialize video with a slight delay to ensure DOM is ready
    this.$nextTick(() => {
      this.initVideo();
    });

    // Add resize listener to ensure video covers the screen properly
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    // Clean up event listeners
    window.removeEventListener("resize", this.handleResize);

    const video = this.$refs.backgroundVideo;
    if (video) {
      video.removeEventListener("loadeddata", this.videoLoaded);
      video.removeEventListener("error", this.videoError);
    }
  },
};
</script>

<style lang="scss" scoped>
.home-section {
  position: relative;
  height: 100vh;
  display: flex;
  align-items: center;
  color: white;
  overflow: hidden;

  // Video background
  .video-background {
    position: absolute;
    top: 50%;
    left: 50%;
    min-width: 100%;
    min-height: 100%;
    width: auto;
    height: auto;
    transform: translateX(-50%) translateY(-50%);
    object-fit: cover;
    z-index: -2;
    will-change: transform; // Performance optimization
  }

  // Fallback background if video fails
  &.video-fallback {
    background: url("@/assets/Home/fallback-background.jpg") no-repeat center
      center;
    background-size: cover;
  }

  // Overlay gradient
  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to right,
      rgba(0, 0, 0, 0.8),
      rgba(0, 0, 0, 0.5)
    );
    z-index: -1;
  }

  // Video play button (appears if autoplay is blocked)
  .video-play-button {
    position: absolute;
    top: 20px;
    right: 20px;
    padding: 10px 15px;
    background: rgba(238, 128, 10, 0.9);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    z-index: 10;
    font-weight: 500;

    &:hover {
      background: #ff7043;
    }
  }

  .container {
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 1;
  }

  // Content Box
  .content-box {
    max-width: 600px;
    padding: 2rem 0;

    .pre-title {
      text-transform: uppercase;
      letter-spacing: 3px;
      font-size: 0.9rem;
      font-weight: 500;
      color: #ee800a;
      margin-bottom: 1rem;
    }

    .main-title {
      font-size: 4.5rem;
      font-weight: 700;
      margin-bottom: 1rem;
      color: #ffffff;
      letter-spacing: -0.5px;
      line-height: 1.1;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3); // Added for better readability

      @media (max-width: 768px) {
        font-size: 3rem;
      }
    }

    .title-underline {
      width: 80px;
      height: 3px;
      background: linear-gradient(to right, #ee800a, #ff7043);
      margin: 0 0 1.5rem;
      border-radius: 2px;
    }

    .main-tagline {
      font-size: 1.8rem;
      font-weight: 300;
      margin-bottom: 2.5rem;
      color: #ffffff;
      border-left: 3px solid #ee800a;
      padding-left: 1rem;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2); // Added for better readability

      @media (max-width: 768px) {
        font-size: 1.4rem;
      }
    }
  }

  // Info Items
  .brief-info {
    margin-bottom: 3rem;

    .info-item {
      display: flex;
      align-items: center;
      margin-bottom: 0.75rem;

      .info-icon {
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 0.75rem;
        color: #ee800a;

        svg {
          width: 100%;
          height: 100%;
        }
      }

      span {
        font-size: 1.1rem;
        font-weight: 400;
        color: #f5f5f5;
      }
    }

    .tagline-text {
      margin-top: 1.5rem;
      font-size: 1.2rem;
      line-height: 1.6;
      color: #f5f5f5;
      font-weight: 300;
    }
  }

  // Call to Action Buttons Container
  .cta-buttons {
    display: flex;
    gap: 20px;

    @media (max-width: 480px) {
      flex-direction: column;
      gap: 15px;
    }
  }

  // Call to Action Button - Base Styles
  .cta-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 1rem 2rem;
    font-size: 1rem;
    font-weight: 500;
    color: #fff;
    border: none;
    border-radius: 30px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    text-transform: uppercase;
    letter-spacing: 1px;
    position: relative;
    overflow: hidden;
    z-index: 1;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);

    &:before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 0;
      height: 100%;
      z-index: -1;
      transition: width 0.4s ease;
    }

    &:hover {
      &:before {
        width: 100%;
      }

      .button-icon {
        transform: translateX(5px);
      }
    }

    .button-icon {
      margin-left: 10px;
      transition: transform 0.3s ease;
      display: flex;
      align-items: center;
    }
  }

  // Inventory Button - Specific Styles
  .inventory-button {
    background: linear-gradient(135deg, #ee800a, #ff7043);

    &:before {
      background: rgba(9, 40, 63, 0.9);
    }

    &:hover {
      box-shadow: 0 10px 25px rgba(9, 40, 63, 0.3);
    }
  }

  // Application Button - Specific Styles
  .application-button {
    background: linear-gradient(135deg, #2980b9, #3498db);

    &:before {
      background: rgba(41, 128, 185, 0.9);
    }

    &:hover {
      box-shadow: 0 10px 25px rgba(41, 128, 185, 0.3);

      .button-icon svg {
        transform: translateY(-2px);
      }
    }

    .button-icon svg {
      transition: transform 0.3s ease;
    }
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .home-section {
    .content-box {
      padding: 1.5rem 0;
    }

    .cta-button {
      padding: 0.85rem 1.75rem;
      font-size: 0.9rem;
    }
  }
}

@media (max-width: 480px) {
  .home-section {
    .content-box {
      .main-title {
        font-size: 2.5rem;
      }

      .main-tagline {
        font-size: 1.2rem;
      }
    }
  }
}
</style>
